import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { AxiosError } from 'axios';
import { orgs, type Organization, type OrganizationCreate } from '@/api/catalogs';
import { taxOffices } from '@/api/taxOffices';
import { resolveOrgOfficials } from '@/api/hr';
import { refs, type BankAccount } from '@/api/refs';
import { businessTemplates, type AffectedDocs } from '@/api/admin';
import Drawer from '@/components/Drawer';
import DatePopover from '@/components/DatePopover';
import { isDirty } from '@/utils/dirty';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { OrgWarehousesTab, OrgCashRegistersTab } from '@/pages/organizations/OrgRefsTabs';
import { useConfirm } from '@/components/ConfirmProvider';
import { ConfirmModuleNarrowingModal } from '@/components/ConfirmModuleNarrowingModal';
import {
  OPFG_CODES,
  findOpfgName,
  findBankByIban,
  formatIban,
  isValidUaIban,
  extractMfoFromIban,
  type BankRow,
} from '@/shared/ukr_classifiers';

const EMPTY: OrganizationCreate = {
  // Auto-code (2026-06-02): null → backend генерує "ORG-N".
  code: null,
  name: '',
  full_name: '',
  entity_type: 'LEGAL_ENTITY',
  edrpou: null,
  inn: null,
  is_vat_payer: true,
  vat_number: null,
  legal_address: null,
  actual_address: null,
  phone: null,
  email: null,
  director_name: null,
  chief_accountant: null,
  director_inn: null,
  chief_accountant_inn: null,
  sti_region: null,
  sti_district: null,
  sti_name: null,
  sti_code: null,
  cost_method: 'WAVG',
  production_costing_mode: 'SIMPLE',
  tax_system: 'GENERAL',
  // TD-B-P5.2.3 (2026-06-01): default 24 — 1С УТП-практика ПФУ.
  maternity_calc_period_months: 24,
  base_currency_code: 'UAH',
  business_template_id: null,
  use_double_entry: true,
  use_chart_of_accounts: true,
  use_recipes: false,
  use_retail: false,
  use_directions: false,
  use_returnable_packaging: false,
  tax_pdfo_recipient_name: null,
  tax_pdfo_recipient_edrpou: null,
  tax_pdfo_recipient_iban: null,
  tax_wz_recipient_name: null,
  tax_wz_recipient_edrpou: null,
  tax_wz_recipient_iban: null,
  tax_esv_recipient_name: null,
  tax_esv_recipient_edrpou: null,
  tax_esv_recipient_iban: null,
  registration_date: null,
  kved_main: null,
  kved_extra: [],
  // ORG-EDITOR.1 (2026-06-04): ОПФГ + КОАТУУ.
  opfg_code: null,
  opfg_name: null,
  koatuu_code: null,
};

// ORG-EDITOR.1 (2026-06-04): таби bottom-block у формі організації.
type OrgTab = 'codes' | 'kved' | 'contacts' | 'bank' | 'warehouses' | 'cash-registers' | 'tax';

/**
 * ORG-EDITOR.3 hotfix (2026-06-04): форматує помилку save mutation
 * у читабельний рядок для inline error block у Drawer'і.
 *
 * Backend може повернути:
 * - 422 з detail = Array<{loc, msg, type}> (Pydantic validation)
 * - 4xx/5xx з detail = string (HTTPException)
 * - Network error без response
 */
function formatSaveError(err: unknown): string {
  if (!err) return 'Невідома помилка';
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      | undefined;
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((e) => {
          const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
          return field ? `${field}: ${e.msg}` : e.msg ?? '';
        })
        .filter(Boolean)
        .join('; ');
    }
    return err.message;
  }
  return String(err);
}

// Рішення власника 2026-06-02: checksum прибрана, лише формат.
// Прецедент: ЄДРПОУ 34927739, 40075815 (Розетка), 39119549 (Нова Пошта)
// — реальні юр.особи, але не проходять стандартний алгоритм Держстату.
function isValidEdrpou(v: string): boolean {
  return /^\d{8}$/.test(v);
}

function isValidRnokpp(v: string): boolean {
  return /^\d{10}$/.test(v);
}

function isValidKved(v: string): boolean {
  return /^\d{2}\.\d{1,2}$/.test(v);
}

export default function OrganizationsPage() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Збереження ширини колонок між перезавантаженнями сторінки.
  const colW = useGridColumnWidths('organizations');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Organization | 'new' | null>(null);
  const [form, setForm] = useState<OrganizationCreate>(EMPTY);
  // ORG-EDITOR.1 (2026-06-04): таби нижнього блоку.
  const [activeTab, setActiveTab] = useState<OrgTab>('codes');
  // Права per-org для вкладок Склади/Каси (паттерн view=wide, edit=narrow):
  // список видно завжди (RLS), кнопки create/edit/delete — лише з refs.warehouses.edit.
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const orgIdForRefs = editing && editing !== 'new' ? editing.id : '';
  const canManageRefs = orgIdForRefs
    ? (permissionsByOrg[orgIdForRefs] ?? []).includes('refs.warehouses.edit')
    : false;
  const refsCantReason = 'Немає права створювати/змінювати склади/каси у цій юрособі '
    + '(потрібен refs.warehouses.edit)';
  // Inline bank-account (при створенні нового org) — оптіональні поля.
  // При зберіганні зашлемо okремий POST /refs/bank-accounts якщо iban не порожній.
  // is_main: true за default бо це перший рахунок (логічно).
  const [newBankIban, setNewBankIban] = useState('');
  const [newBankCurrencyId, setNewBankCurrencyId] = useState('');
  const [newBankAccountName, setNewBankAccountName] = useState('');
  const [newBankIsMain, setNewBankIsMain] = useState(true);
  const [resolvingOfficials, setResolvingOfficials] = useState(false);
  // Пошук ДПІ (вкладка «Коди»): юзер обирає свою податкову зі списку —
  // проставляються код ДПІ (C_STI_ORIG), область (C_REG) і назва (HSTI).
  const [stiSearch, setStiSearch] = useState('');
  const [stiOpen, setStiOpen] = useState(false);
  const stiQ = useQuery({
    queryKey: ['tax-offices', stiSearch],
    queryFn: () => taxOffices.list({ q: stiSearch || undefined, limit: 30 }),
    enabled: stiOpen,
    staleTime: 1000 * 60 * 30,
  });

  async function onResolveOfficials() {
    if (!orgIdForRefs) return;
    setResolvingOfficials(true);
    try {
      const res = await resolveOrgOfficials(orgIdForRefs);
      const u = res.updated;
      if (u.director_name || u.chief_accountant) {
        setForm((f) => ({
          ...f,
          director_name: u.director_name ?? f.director_name,
          director_inn: u.director_inn ?? f.director_inn,
          chief_accountant: u.chief_accountant ?? f.chief_accountant,
          chief_accountant_inn: u.chief_accountant_inn ?? f.chief_accountant_inn,
        }));
        alert('Підтягнуто з наказів: '
          + [u.director_name && `керівник ${u.director_name}`,
             u.chief_accountant && `головбух ${u.chief_accountant}`]
            .filter(Boolean).join(', '));
      } else {
        alert('У проведених наказах не знайдено керівника/головного бухгалтера '
          + `(перевірено ${res.found_orders} наказів). Впишіть вручну.`);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail ?? 'Помилка');
    } finally {
      setResolvingOfficials(false);
    }
  }

  const list = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const templatesQ = useQuery({
    queryKey: ['admin-templates'],
    queryFn: () => businessTemplates.list(),
  });
  // Currencies для inline bank-account UI.
  const currenciesQ = useQuery({
    queryKey: ['currencies'],
    queryFn: () => refs.currencies(),
  });
  // ORG-EDITOR.2 (2026-06-04): banks lookup з NBU для auto-bank-name.
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks(),
    staleTime: 1000 * 60 * 60, // 1h — банки рідко змінюються
  });
  // Bank accounts existing org. NULL при створенні нового — refetch skip.
  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', editing && editing !== 'new' ? editing.id : null],
    queryFn: () => refs.bankAccounts(
      editing && editing !== 'new' ? editing.id : undefined,
    ),
    enabled: editing !== null && editing !== 'new',
  });
  const create = useMutation({
    mutationFn: async (p: OrganizationCreate) => {
      const org = await orgs.create(p);
      // ORG-EDITOR.1 (2026-06-04): якщо при створенні вказано IBAN —
      // створюємо bank-account inline.
      if (newBankIban.trim() && newBankCurrencyId) {
        try {
          await refs.createBankAccount({
            organization_id: org.id,
            iban: newBankIban.replace(/\s+/g, '').toUpperCase(),
            currency_id: newBankCurrencyId,
            account_name: newBankAccountName.trim() || null,
            is_main: newBankIsMain,
          });
        } catch (e) {
          // Soft-fail: organization створена, але bank-account не вдалося.
          // Користувач зможе додати його руками при редагуванні.
          console.warn('Inline bank-account creation failed:', e);
        }
      }
      return org;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs'] });
      setEditing(null);
      // Скидаємо inline bank state.
      setNewBankIban(''); setNewBankCurrencyId(''); setNewBankAccountName('');
    },
  });
  const update = useMutation({
    mutationFn: ({ id, p }: { id: string; p: Partial<OrganizationCreate> }) => orgs.update(id, p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['orgs'] }); setEditing(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => orgs.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  });
  // ORG-EDITOR.1 (2026-06-04): mutations для bank-accounts при редагуванні.
  const addBankAccount = useMutation({
    mutationFn: (p: { iban: string; currency_id: string; account_name: string | null }) => {
      if (!editing || editing === 'new') throw new Error('No org selected');
      return refs.createBankAccount({
        organization_id: editing.id,
        iban: p.iban.replace(/\s+/g, '').toUpperCase(),
        currency_id: p.currency_id,
        account_name: p.account_name,
        is_main: bankAccountsQ.data?.length === 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
  const removeBankAccount = useMutation({
    mutationFn: (id: string) => refs.removeBankAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
  // ORG-EDITOR.2 (2026-06-04): set main — backend auto-unset попередній main.
  const setMainBankAccount = useMutation({
    mutationFn: (id: string) => refs.updateBankAccount(id, { is_main: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });

  const cols = useMemo<ColDef<Organization>[]>(() => [
    { field: 'code', headerName: t('org.code'), width: 110 },
    { field: 'name', headerName: t('org.name'), flex: 1, minWidth: 220 },
    { field: 'edrpou', headerName: t('org.edrpou'), width: 130 },
    {
      field: 'is_vat_payer',
      headerName: t('org.is_vat_payer'),
      width: 130,
      valueFormatter: (p) => (p.value ? t('common.yes') : t('common.no')),
    },
    { field: 'cost_method', headerName: t('org.cost_method'), width: 130 },
    { field: 'tax_system', headerName: t('org.tax_system'), width: 150 },
  ], [t]);

  function startCreate() {
    setForm(EMPTY);
    setEditing('new');
    setActiveTab('codes');
    setNewBankIban(''); setNewBankCurrencyId(''); setNewBankAccountName('');
  }
  function startEdit(o: Organization) {
    setForm({
      code: o.code, name: o.name, full_name: o.full_name,
      entity_type: o.entity_type,
      edrpou: o.edrpou, inn: o.inn,
      is_vat_payer: o.is_vat_payer, vat_number: o.vat_number,
      legal_address: o.legal_address,
      actual_address: o.actual_address,
      phone: o.phone, email: o.email,
      director_name: o.director_name,
      chief_accountant: o.chief_accountant,
      director_inn: o.director_inn ?? null,
      chief_accountant_inn: o.chief_accountant_inn ?? null,
      sti_region: o.sti_region ?? null,
      sti_district: o.sti_district ?? null,
      sti_name: o.sti_name ?? null,
      sti_code: o.sti_code ?? null,
      cost_method: o.cost_method, tax_system: o.tax_system,
      production_costing_mode: o.production_costing_mode ?? 'SIMPLE',
      // TD-B-P5.2.3 (2026-06-01).
      maternity_calc_period_months: o.maternity_calc_period_months ?? 24,
      base_currency_code: o.base_currency_code,
      business_template_id: o.business_template_id,
      use_double_entry: o.use_double_entry,
      use_chart_of_accounts: o.use_chart_of_accounts,
      use_recipes: o.use_recipes,
      use_retail: o.use_retail,
      use_directions: o.use_directions,
      use_returnable_packaging: o.use_returnable_packaging,
      tax_pdfo_recipient_name: o.tax_pdfo_recipient_name,
      tax_pdfo_recipient_edrpou: o.tax_pdfo_recipient_edrpou,
      tax_pdfo_recipient_iban: o.tax_pdfo_recipient_iban,
      tax_wz_recipient_name: o.tax_wz_recipient_name,
      tax_wz_recipient_edrpou: o.tax_wz_recipient_edrpou,
      tax_wz_recipient_iban: o.tax_wz_recipient_iban,
      tax_esv_recipient_name: o.tax_esv_recipient_name,
      tax_esv_recipient_edrpou: o.tax_esv_recipient_edrpou,
      tax_esv_recipient_iban: o.tax_esv_recipient_iban,
      registration_date: o.registration_date,
      kved_main: o.kved_main,
      kved_extra: o.kved_extra,
      // ORG-EDITOR.1 (2026-06-04): ОПФГ + КОАТУУ.
      opfg_code: o.opfg_code,
      opfg_name: o.opfg_name,
      koatuu_code: o.koatuu_code,
    });
    setActiveTab('codes');
    setEditing(o);
  }
  // Confirmation modal стан для перемикання шаблону зі звуженням модулів.
  const [templateSwitchConfirm, setTemplateSwitchConfirm] = useState<{
    open: boolean;
    removedModules: string[];
    affected: AffectedDocs | null;
    isLoading: boolean;
  }>({ open: false, removedModules: [], affected: null, isLoading: false });

  async function save() {
    if (editing === 'new') {
      create.mutate(form);
      return;
    }
    if (!editing) return;
    const oldTplId = (editing as Organization).business_template_id;
    const newTplId = form.business_template_id;

    // Якщо template НЕ змінився, або переходимо з/у null — звичайний save.
    if (oldTplId === newTplId || !oldTplId || !newTplId) {
      update.mutate({ id: editing.id, p: form });
      return;
    }

    // Перевірити чи новий шаблон ВУЖЧИЙ за старий за модулями.
    const oldTpl = templatesQ.data?.find((t) => t.id === oldTplId);
    const newTpl = templatesQ.data?.find((t) => t.id === newTplId);
    if (!oldTpl || !newTpl) {
      update.mutate({ id: editing.id, p: form });
      return;
    }
    const removed = oldTpl.enabled_modules.filter(
      (m) => !newTpl.enabled_modules.includes(m),
    );
    if (removed.length === 0) {
      update.mutate({ id: editing.id, p: form });
      return;
    }

    // Показати confirmation з підрахунком документів цього org'а.
    setTemplateSwitchConfirm({
      open: true, removedModules: removed, affected: null, isLoading: true,
    });
    try {
      const data = await businessTemplates.affectedDocs(
        newTplId, removed, editing.id,
      );
      setTemplateSwitchConfirm((s) => ({ ...s, affected: data, isLoading: false }));
    } catch {
      // На помилку affected-docs — показати модалку з 0 (failed-soft).
      setTemplateSwitchConfirm((s) => ({
        ...s, affected: { total: 0, by_module: {} }, isLoading: false,
      }));
    }
  }

  function confirmTemplateSwitch() {
    if (!editing || editing === 'new') return;
    setTemplateSwitchConfirm((s) => ({ ...s, open: false }));
    update.mutate({ id: editing.id, p: form });
  }

  const rows = list.data?.items ?? [];
  const filtered = search
    ? rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())
        || (r.edrpou ?? '').includes(search))
    : rows;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{t('org.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="primary" onClick={startCreate}>+ {t('org.new')}</button>
        </div>
      </div>
      <div className="ag-theme-quartz grid">
        <AgGridReact<Organization>
          rowData={filtered}
          columnDefs={cols}
          loading={list.isLoading}
          onRowDoubleClicked={(e) => e.data && startEdit(e.data)}
          rowSelection="single"
          getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
          onColumnResized={colW.onColumnResized}
          onGridReady={(e) => { colW.applyTo(e.api); }}
        />
      </div>

      <Drawer
        open={editing !== null}
        width="wide"
        title={editing === 'new' ? t('org.new') : (editing as Organization | null)?.name ?? ''}
        onClose={() => setEditing(null)}
        dirty={editing !== null && isDirty(form, editing === 'new' ? null : editing as unknown as Partial<OrganizationCreate>)}
        headerActions={
          <>
            {editing !== 'new' && editing && (
              <button
                className="danger"
                onClick={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) { remove.mutate(editing.id); setEditing(null); } }) }}
              >
                {t('common.delete')}
              </button>
            )}
            <button onClick={() => setEditing(null)}>{t('common.cancel')}</button>
            <button className="primary" onClick={save} disabled={create.isPending || update.isPending}>
              {t('common.save')}
            </button>
          </>
        }
      >
        {/* Inline помилка save — щоб не покладатись лише на глобальний toast.
            (2026-06-04 ORG-EDITOR.3 hotfix: власник скаржився «не сохраняет
            рахунок» — токен швидко зникав і не зрозуміло було що падає). */}
        {(create.isError || update.isError) && (
          <div style={{
            background: '#fee', border: '1px solid #fcc', padding: '0.75rem',
            borderRadius: 6, marginBottom: '0.75rem', color: '#b00',
          }}>
            <strong>❌ Не вдалось зберегти:</strong>
            <div style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
              {formatSaveError(create.error ?? update.error)}
            </div>
          </div>
        )}

        {/* === БЛОК 1: Тип сутності + основна інфо === */}
        <div style={{ margin: "0.75rem 0" }}>
          <h4 style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)" }}>Тип організації</h4>
          <div className="field" style={{ display: 'flex', gap: '1.5rem' }}>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="radio" name="entity_type"
                checked={form.entity_type === 'LEGAL_ENTITY'}
                onChange={() => {
                  const newTax = form.entity_type === 'LEGAL_ENTITY'
                    ? form.tax_system
                    : 'GENERAL';
                  setForm({
                    ...form, entity_type: 'LEGAL_ENTITY',
                    tax_system: newTax as OrganizationCreate['tax_system'],
                  });
                }}
                style={{ width: 'auto', marginRight: '0.35rem' }}
              />
              ТОВ (юрособа)
            </label>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="radio" name="entity_type"
                checked={form.entity_type === 'FOP'}
                onChange={() => {
                  const newTax = form.entity_type === 'FOP'
                    ? form.tax_system
                    : 'FOP_2';
                  setForm({
                    ...form, entity_type: 'FOP',
                    tax_system: newTax as OrganizationCreate['tax_system'],
                  });
                }}
                style={{ width: 'auto', marginRight: '0.35rem' }}
              />
              ФОП (фізособа-підприємець)
            </label>
          </div>
        </div>

        <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc', marginBottom: '0.75rem' }}>
          <legend>Облікові модулі</legend>
          <label style={{ cursor: 'pointer', display: 'block' }}>
            <input
              type="checkbox"
              checked={form.use_returnable_packaging ?? false}
              onChange={(e) => setForm({ ...form, use_returnable_packaging: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Облік зворотної (заставної) тари
            <small style={{ color: 'var(--muted)', display: 'block', marginLeft: '1.5rem' }}>
              Увімкніть, якщо ведете заставну багатооборотну тару (ящики, піддони, бочки).
              Без цього документи тари недоступні.
            </small>
          </label>

          <div className="field" style={{ marginTop: '0.75rem' }}>
            <label>Облік виробництва</label>
            <select
              value={form.production_costing_mode ?? 'SIMPLE'}
              onChange={(e) =>
                setForm({
                  ...form,
                  production_costing_mode: e.target.value as 'SIMPLE' | '1C_WIP',
                })
              }
            >
              <option value="SIMPLE">
                Спрощений — матеріали одразу в собівартість (без НЗВ)
              </option>
              <option value="1C_WIP">
                Повний — облік незавершеного виробництва (НЗВ), як у 1С
              </option>
            </select>
            <small style={{ color: 'var(--muted)', display: 'block' }}>
              <b>Спрощений</b> — для торгівлі або коли виробництво завжди завершується в
              періоді (рах. 23 не накопичує залишок). <b>Повний (НЗВ)</b> — для виробництва з
              незавершеними залишками на кінець місяця: випуск за плановою + закриття місяця
              рахує фактичну собівартість. Змінити можна лише доки немає проведених
              виробничих документів.
            </small>
          </div>
        </fieldset>

        <div className="field">
          <label>{t('org.code')}</label>
          <input
            value={form.code ?? ''}
            onChange={(e) => setForm({ ...form, code: e.target.value || null })}
            placeholder="ORG-1 (auto)"
          />
        </div>
        <div className="field">
          <label>{t('org.name')} * <small style={{ color: 'var(--muted)' }}>(коротка)</small></label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="field">
          <label>{t('org.full_name')} * <small style={{ color: 'var(--muted)' }}>(повна, як у виписці)</small></label>
          <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </div>
        <div className="field">
          <label>Бізнес-шаблон</label>
          <select
            value={form.business_template_id ?? ''}
            onChange={(e) => setForm({ ...form, business_template_id: e.target.value || null })}
          >
            <option value="">— Без шаблону —</option>
            {templatesQ.data?.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
          {editing === 'new' && (
            <small style={{ color: 'var(--muted)' }}>
              При створенні застосує налаштування облікової політики шаблону.
            </small>
          )}
        </div>

        {/* === БЛОК 2: Ідентифікація (умовно ЄДРПОУ або РНОКПП) === */}
        <div style={{ margin: "0.75rem 0" }}>
          <h4 style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)" }}>Ідентифікація</h4>
          {form.entity_type === 'LEGAL_ENTITY' ? (
            <div className="field">
              <label>{t('org.edrpou')} * <small style={{ color: 'var(--muted)' }}>(8 цифр)</small></label>
              <input
                value={form.edrpou ?? ''}
                maxLength={8}
                placeholder="12345678"
                onChange={(e) => setForm({
                  ...form, edrpou: e.target.value.replace(/\D/g, '') || null,
                })}
              />
              {form.edrpou && (
                <small style={{ color: isValidEdrpou(form.edrpou) ? 'green' : '#d33' }}>
                  {isValidEdrpou(form.edrpou)
                    ? '✓ Контрольна сума правильна'
                    : form.edrpou.length === 8
                      ? '✗ Невірна контрольна сума (перевірте номер у виписці)'
                      : `✗ Має бути 8 цифр (зараз ${form.edrpou.length})`
                  }
                </small>
              )}
            </div>
          ) : (
            <div className="field">
              <label>РНОКПП * <small style={{ color: 'var(--muted)' }}>(10 цифр)</small></label>
              <input
                value={form.inn ?? ''}
                maxLength={10}
                placeholder="1234567890"
                onChange={(e) => setForm({
                  ...form, inn: e.target.value.replace(/\D/g, '') || null,
                })}
              />
              {form.inn && (
                <small style={{ color: isValidRnokpp(form.inn) ? 'green' : '#d33' }}>
                  {isValidRnokpp(form.inn)
                    ? '✓ Контрольна сума правильна'
                    : form.inn.length === 10
                      ? '✗ Невірна контрольна сума'
                      : `✗ Має бути 10 цифр (зараз ${form.inn.length})`
                  }
                </small>
              )}
            </div>
          )}
        </div>

        {/* === БЛОК 3: Система оподаткування === */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="field">
            <label>{t('org.cost_method')} *</label>
            <select value={form.cost_method} onChange={(e) => setForm({ ...form, cost_method: e.target.value as 'FIFO' | 'WAVG' })}>
              <option value="WAVG">WAVG (середньозважена)</option>
              <option value="FIFO">FIFO</option>
            </select>
          </div>
          <div className="field">
            <label>{t('org.tax_system')} *</label>
            <select
              value={form.tax_system}
              onChange={(e) => {
                const newTs = e.target.value as OrganizationCreate['tax_system'];
                // Smart cascade: FOP-групи з фіксованим ПДВ-статусом.
                let vatFixed: boolean | null = null;
                if (newTs === 'FOP_1' || newTs === 'FOP_2' || newTs === 'FOP_3_5') vatFixed = false;
                if (newTs === 'FOP_3_3') vatFixed = true;
                setForm({
                  ...form,
                  tax_system: newTs,
                  is_vat_payer: vatFixed !== null ? vatFixed : form.is_vat_payer,
                  // Якщо знімаємо ПДВ-статус — скидаємо номер свідоцтва.
                  vat_number: vatFixed === false ? null : form.vat_number,
                });
              }}
            >
              {form.entity_type === 'LEGAL_ENTITY' ? (
                <>
                  <option value="GENERAL">Загальна (податок на прибуток 18%)</option>
                  <option value="SIMPLIFIED_3">Спрощена 3 група (ТОВ)</option>
                  <option value="SIMPLIFIED_4">Спрощена 4 (с/г ТОВ)</option>
                </>
              ) : (
                <>
                  <option value="FOP_GENERAL">ФОП на загальній (ст. 177 ПКУ)</option>
                  <option value="FOP_1">ФОП 1 група (єд. податок)</option>
                  <option value="FOP_2">ФОП 2 група (єд. податок)</option>
                  <option value="FOP_3_3">ФОП 3 група 3% (платник ПДВ)</option>
                  <option value="FOP_3_5">ФОП 3 група 5% (неплатник ПДВ)</option>
                </>
              )}
            </select>
          </div>
        </div>
        {/* TD-B-P5.2.3: MATERNITY calc period + облік тари — в одному рядку. */}
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ minWidth: 260 }}>
            <label>Розрахунковий період для декретних *</label>
            <select
              value={form.maternity_calc_period_months ?? 24}
              onChange={(e) => setForm({
                ...form,
                maternity_calc_period_months: parseInt(e.target.value, 10) as 12 | 24,
              })}
            >
              <option value={24}>24 місяці</option>
              <option value={12}>12 місяців</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ paddingBottom: '0.45rem' }}>
          {(() => {
            const ts = form.tax_system;
            const vatFixed =
              ts === 'FOP_1' || ts === 'FOP_2' || ts === 'FOP_3_5' ? false :
              ts === 'FOP_3_3' ? true : null;
            return (
              <label>
                <input
                  type="checkbox"
                  checked={form.is_vat_payer}
                  disabled={vatFixed !== null}
                  onChange={(e) => setForm({ ...form, is_vat_payer: e.target.checked })}
                  style={{ width: 'auto', marginRight: '0.5rem' }}
                />
                {t('org.is_vat_payer')}
                {vatFixed !== null && (
                  <small style={{ color: 'var(--muted)', marginLeft: 6 }}>
                    (фіксовано системою оподаткування)
                  </small>
                )}
              </label>
            );
          })()}
          {form.entity_type === 'FOP' && form.tax_system === 'FOP_3_3' && !form.is_vat_payer && (
            <small style={{ color: '#d33' }}>✗ ФОП 3 група 3% — обов'язковий платник ПДВ</small>
          )}
          {form.entity_type === 'FOP' && form.tax_system === 'FOP_3_5' && form.is_vat_payer && (
            <small style={{ color: '#d33' }}>✗ ФОП 3 група 5% — не може бути платником ПДВ</small>
          )}
          {form.entity_type === 'FOP' && (form.tax_system === 'FOP_1' || form.tax_system === 'FOP_2') && form.is_vat_payer && (
            <small style={{ color: '#d33' }}>✗ ФОП 1/2 групи не можуть бути платниками ПДВ</small>
          )}
        </div>
        {form.is_vat_payer && (
          <div className="field" style={{ minWidth: 200 }}>
            <label>{t('org.vat_number')} * <small style={{ color: 'var(--muted)' }}>(9-12 цифр)</small></label>
            <input
              value={form.vat_number ?? ''}
              maxLength={12}
              placeholder="123456789"
              onChange={(e) => setForm({
                ...form, vat_number: e.target.value.replace(/\D/g, '') || null,
              })}
            />
          </div>
        )}
        </div>
        {form.entity_type === 'FOP' && (
          <div className="field">
            <label>Дата державної реєстрації *</label>
            <DatePopover
              value={form.registration_date ?? ''}
              onChange={(v) => setForm({ ...form, registration_date: v || null })}
            />
          </div>
        )}

        {/* ===================================================================
            ORG-EDITOR.1 (2026-06-04): нижній блок з табами
            - Коди (ОПФГ + КОАТУУ)
            - Контакти (адреси + телефон/email + відповідальні особи)
            - Банк (IBAN + currency, auto-fill банку з МФО)
        =================================================================== */}
        <div className="seg-tabs" style={{ marginTop: "1rem" }}>
          <button
            type="button"
            className={activeTab === 'codes' ? 'active' : ''}
            onClick={() => setActiveTab('codes')}
          >
            Коди
          </button>
          <button
            type="button"
            className={activeTab === 'kved' ? 'active' : ''}
            onClick={() => setActiveTab('kved')}
          >
            КВЕД
          </button>
          <button
            type="button"
            className={activeTab === 'contacts' ? 'active' : ''}
            onClick={() => setActiveTab('contacts')}
          >
            Контакти
          </button>
          <button
            type="button"
            className={activeTab === 'bank' ? 'active' : ''}
            onClick={() => setActiveTab('bank')}
          >
            Банк
          </button>
          <button
            type="button"
            className={activeTab === 'warehouses' ? 'active' : ''}
            onClick={() => setActiveTab('warehouses')}
          >
            Склади
          </button>
          <button
            type="button"
            className={activeTab === 'cash-registers' ? 'active' : ''}
            onClick={() => setActiveTab('cash-registers')}
          >
            Каси
          </button>
          <button
            type="button"
            className={activeTab === 'tax' ? 'active' : ''}
            onClick={() => setActiveTab('tax')}
          >
            Податкові рахунки
          </button>
        </div>

        {/* === ТАБ: Коди (ОПФГ + КОАТУУ) === */}
        {activeTab === 'codes' && (
          <div className="tabs__panel">
            <div className="field">
              <label>
                Організаційно-правова форма (ОПФГ)
                <small style={{ color: 'var(--muted)' }}> (ДК 002:2004)</small>
              </label>
              <select
                value={form.opfg_code ?? ''}
                onChange={(e) => {
                  const code = e.target.value || null;
                  setForm({
                    ...form,
                    opfg_code: code,
                    opfg_name: findOpfgName(code),
                  });
                }}
              >
                <option value="">— Не вказано —</option>
                {OPFG_CODES.map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.code} — {o.name}
                  </option>
                ))}
              </select>
              {form.opfg_code && form.opfg_name && (
                <small style={{ color: 'var(--muted)' }}>
                  Код: {form.opfg_code}, назва: {form.opfg_name}
                </small>
              )}
            </div>
            <div className="field" style={{ marginTop: '0.75rem' }}>
              <label>
                Код КАТОТТГ / КОАТУУ
                <small style={{ color: 'var(--muted)' }}>
                  {' '}(КАТОТТГ: UA + 17 цифр — для звітності ДПС; старий КОАТУУ: 10 цифр)
                </small>
              </label>
              <input
                value={form.koatuu_code ?? ''}
                maxLength={19}
                placeholder="UA80000000000093317 (м. Київ)"
                onChange={(e) => setForm({
                  ...form,
                  koatuu_code: e.target.value.toUpperCase().replace(/[^UA0-9]/g, '') || null,
                })}
              />
              {form.koatuu_code && !/^(\d{10}|UA\d{17})$/.test(form.koatuu_code) && (
                <small style={{ color: '#d33' }}>
                  ✗ UA + 17 цифр (КАТОТТГ) або 10 цифр (КОАТУУ)
                </small>
              )}
            </div>

            {/* Ваша ДПІ (податкова інспекція) — вибір зі справочника.
                Проставляє код ДПІ (C_STI_ORIG), область (C_REG) і назву (HSTI). */}
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem',
              borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                Ваша податкова інспекція (ДПІ)
              </div>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap',
                alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: '1 1 360px', position: 'relative' }}>
                  <label>Оберіть ДПІ (пошук за назвою, кодом або областю)</label>
                  <input
                    value={stiOpen ? stiSearch : (form.sti_name
                      ? `${form.sti_code ?? ''} — ${form.sti_name}` : '')}
                    placeholder="напр. «Львівська» або 1305"
                    onFocus={() => { setStiOpen(true); setStiSearch(''); }}
                    onChange={(e) => { setStiSearch(e.target.value); setStiOpen(true); }}
                    onBlur={() => setTimeout(() => setStiOpen(false), 150)} />
                  {stiOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
                      zIndex: 20, background: '#fff', border: '1px solid #e2e8f0',
                      borderRadius: 6, marginTop: 2, maxHeight: 240, overflow: 'auto',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                      {(stiQ.data?.items ?? []).map((o) => (
                        <div key={o.code}
                          onMouseDown={() => {
                            setForm({ ...form,
                              sti_code: o.code,
                              sti_name: o.name,
                              sti_region: o.code.length >= 3
                                ? Number(o.code.slice(0, -2)) : form.sti_region });
                            setStiOpen(false);
                          }}
                          style={{ padding: '0.4rem 0.6rem', cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9' }}>
                          <b>{o.code}</b> — {o.name}
                          {o.region_name && (
                            <span style={{ color: 'var(--muted)' }}> · {o.region_name}</span>
                          )}
                        </div>
                      ))}
                      {stiQ.data && stiQ.data.items.length === 0 && (
                        <div style={{ padding: '0.5rem', color: 'var(--muted)' }}>
                          Нічого не знайдено. Довідник ДПІ оновлюється у розділі
                          «Довідники → Органи ДПС».
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="field" style={{ width: 120 }}>
                  <label title="Код ДПІ (C_STI_ORIG)">Код ДПІ</label>
                  <input disabled style={{ background: '#f8fafc' }}
                    value={form.sti_code ?? ''} placeholder="—" />
                </div>
              </div>
              <small style={{ color: 'var(--muted)' }}>
                Оберіть свою ДПІ — код і назва підставляються у звіти та запити до ДПС
                автоматично.
              </small>
            </div>
          </div>
        )}

        {/* === ТАБ: КВЕД-коди === */}
        {activeTab === 'kved' && (
          <div className="tabs__panel">
            <div className="field">
              <label>Основний КВЕД * <small style={{ color: 'var(--muted)' }}>(формат XX.XX)</small></label>
              <input
                value={form.kved_main ?? ''}
                placeholder="62.01"
                maxLength={7}
                onChange={(e) => setForm({ ...form, kved_main: e.target.value || null })}
              />
              {form.kved_main && (
                <small style={{ color: isValidKved(form.kved_main) ? 'green' : '#d33' }}>
                  {isValidKved(form.kved_main) ? '✓ Формат правильний' : '✗ Очікувано XX.XX'}
                </small>
              )}
            </div>
            {form.kved_extra.length > 0 && (
              <div className="field" style={{ marginTop: '0.5rem' }}>
                <label>Додаткові КВЕД</label>
                {form.kved_extra.map((code, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <input
                      value={code}
                      placeholder="63.11"
                      maxLength={7}
                      onChange={(e) => {
                        const next = [...form.kved_extra];
                        next[i] = e.target.value;
                        setForm({ ...form, kved_extra: next });
                      }}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        kved_extra: form.kved_extra.filter((_, j) => j !== i),
                      })}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              style={{ marginTop: '0.5rem' }}
              onClick={() => setForm({ ...form, kved_extra: [...form.kved_extra, ''] })}
            >
              + Додати КВЕД
            </button>
          </div>
        )}

        {/* === ТАБ: Контакти (об'єднано адреси + контакти + відп.особи) === */}
        {activeTab === 'contacts' && (
          <div className="tabs__panel">
            {/* Адреси */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
              <legend>{form.entity_type === 'FOP' ? 'Адреси (реєстрація + місце ведення)' : 'Адреси'}</legend>
              <div className="field">
                <label>{form.entity_type === 'FOP' ? 'Адреса реєстрації (за пропискою)' : 'Юридична адреса'}</label>
                <input
                  value={form.legal_address ?? ''}
                  placeholder="м. Київ, вул. Хрещатик, 1, оф. 101"
                  onChange={(e) => setForm({ ...form, legal_address: e.target.value || null })}
                />
              </div>
              <div className="field" style={{ marginTop: '0.5rem' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.actual_address === null || form.actual_address === form.legal_address}
                    onChange={(e) => setForm({
                      ...form,
                      actual_address: e.target.checked ? form.legal_address : '',
                    })}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Фактична адреса співпадає з {form.entity_type === 'FOP' ? 'адресою реєстрації' : 'юридичною'}
                </label>
              </div>
              {form.actual_address !== null && form.actual_address !== form.legal_address && (
                <div className="field">
                  <label>Фактична адреса</label>
                  <input
                    value={form.actual_address ?? ''}
                    placeholder="м. Київ, вул. ..."
                    onChange={(e) => setForm({ ...form, actual_address: e.target.value || null })}
                  />
                </div>
              )}
            </fieldset>

            {/* Телефон/email */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc', marginTop: '0.75rem' }}>
              <legend>Контактні дані</legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="field">
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={form.phone ?? ''}
                    placeholder="+38 044 ___ __ __"
                    onChange={(e) => setForm({ ...form, phone: e.target.value || null })}
                  />
                </div>
                <div className="field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email ?? ''}
                    placeholder="office@example.com"
                    onChange={(e) => setForm({ ...form, email: e.target.value || null })}
                  />
                </div>
              </div>
            </fieldset>

            {/* Відповідальні особи */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc', marginTop: '0.75rem' }}>
              <legend>Відповідальні особи</legend>
              {/* Керівник/головбух ведуться ЧЕРЕЗ КАДРИ (наказ прийому/
                  переміщення на посаду директора/головбуха). Тут поля read-only
                  для юрособи; редагування — на картці співробітника. FOP і нова
                  організація — редаговані (наказів ще немає). */}
              {(() => {
                const readonly = editing !== 'new' && form.entity_type === 'LEGAL_ENTITY';
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <small style={{ color: 'var(--muted)', flex: '1 1 300px' }}>
                        {readonly
                          ? 'Заповнюються автоматично з кадрів (наказ прийому/переміщення '
                            + 'на посаду «Директор»/«Генеральний директор»/«Головний бухгалтер»). '
                            + 'Редагування — на картці співробітника. РНОКПП потрібен для звітів ДПС.'
                          : 'РНОКПП потрібен для звітів у ДПС.'}
                      </small>
                      {orgIdForRefs && (
                        <button type="button" disabled={resolvingOfficials}
                          onClick={onResolveOfficials}
                          style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                          title="Знайти керівника/головбуха у наявних проведених наказах">
                          {resolvingOfficials ? '…' : 'підтягнути з наказів'}
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <div className="field" style={{ flex: '1 1 260px' }}>
                        <label>{form.entity_type === 'FOP' ? "ПIБ ФОП *" : 'ПIБ керівника'}</label>
                        <input value={form.director_name ?? ''} disabled={readonly}
                          placeholder="Іваненко Іван Іванович"
                          style={readonly ? { background: '#f8fafc' } : undefined}
                          onChange={(e) => setForm({ ...form, director_name: e.target.value || null })} />
                      </div>
                      <div className="field" style={{ width: 160 }}>
                        <label>РНОКПП керівника</label>
                        <input value={form.director_inn ?? ''} maxLength={10} disabled={readonly}
                          placeholder="10 цифр"
                          style={readonly ? { background: '#f8fafc' } : undefined}
                          onChange={(e) => setForm({ ...form,
                            director_inn: e.target.value.replace(/\D/g, '') || null })} />
                      </div>
                    </div>
                    {form.entity_type === 'LEGAL_ENTITY' && (
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        <div className="field" style={{ flex: '1 1 260px' }}>
                          <label>ПIБ головного бухгалтера</label>
                          <input value={form.chief_accountant ?? ''} disabled={readonly}
                            style={readonly ? { background: '#f8fafc' } : undefined}
                            onChange={(e) => setForm({ ...form, chief_accountant: e.target.value || null })} />
                        </div>
                        <div className="field" style={{ width: 160 }}>
                          <label>РНОКПП головбуха</label>
                          <input value={form.chief_accountant_inn ?? ''} maxLength={10} disabled={readonly}
                            placeholder="10 цифр"
                            style={readonly ? { background: '#f8fafc' } : undefined}
                            onChange={(e) => setForm({ ...form,
                              chief_accountant_inn: e.target.value.replace(/\D/g, '') || null })} />
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </fieldset>
          </div>
        )}

        {/* === ТАБ: Банк (IBAN + currency + auto-bank-from-MFO) === */}
        {activeTab === 'bank' && (
          <div className="tabs__panel">
            {editing === 'new' ? (
              /* Створення: 1 inline bank-account оптіонально */
              <BankAccountInlineForm
                iban={newBankIban}
                onIbanChange={setNewBankIban}
                currencyId={newBankCurrencyId}
                onCurrencyChange={setNewBankCurrencyId}
                accountName={newBankAccountName}
                onAccountNameChange={setNewBankAccountName}
                isMain={newBankIsMain}
                onIsMainChange={setNewBankIsMain}
                currencies={currenciesQ.data ?? []}
                banks={banksQ.data ?? []}
                title="Перший банк-рахунок (опціонально)"
                helpText="Можна залишити порожнім — додасте після створення."
              />
            ) : (
              /* Редагування: список наявних + кнопка + Додати */
              <>
                {/* Error UI для bank-account мутацій (2026-06-04 hotfix). */}
                {(addBankAccount.isError || setMainBankAccount.isError
                  || removeBankAccount.isError) && (
                  <div style={{
                    background: '#fee', border: '1px solid #fcc', padding: '0.75rem',
                    borderRadius: 6, marginBottom: '0.75rem', color: '#b00',
                  }}>
                    <strong>❌ Помилка банк-рахунку:</strong>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
                      {formatSaveError(
                        addBankAccount.error ?? setMainBankAccount.error
                        ?? removeBankAccount.error
                      )}
                    </div>
                  </div>
                )}
                <BankAccountsEditList
                  accounts={bankAccountsQ.data ?? []}
                  currencies={currenciesQ.data ?? []}
                  banks={banksQ.data ?? []}
                  onAdd={(p) => addBankAccount.mutate(p)}
                  onRemove={(id) => removeBankAccount.mutate(id)}
                  onSetMain={(id) => setMainBankAccount.mutate(id)}
                  addPending={addBankAccount.isPending}
                />
              </>
            )}
          </div>
        )}

        {/* === ТАБ: Склади === */}
        {activeTab === 'warehouses' && (
          <div className="tabs__panel">
            {editing === 'new' || !orgIdForRefs ? (
              <p style={{ color: 'var(--muted)' }}>
                Спершу збережіть організацію — потім зможете додати склади.
              </p>
            ) : (
              <OrgWarehousesTab
                orgId={orgIdForRefs}
                canManage={canManageRefs}
                cantReason={refsCantReason}
              />
            )}
          </div>
        )}

        {/* === ТАБ: Каси === */}
        {activeTab === 'cash-registers' && (
          <div className="tabs__panel">
            {editing === 'new' || !orgIdForRefs ? (
              <p style={{ color: 'var(--muted)' }}>
                Спершу збережіть організацію — потім зможете додати каси.
              </p>
            ) : (
              <OrgCashRegistersTab
                orgId={orgIdForRefs}
                canManage={canManageRefs}
                cantReason={refsCantReason}
              />
            )}
          </div>
        )}

        {/* === ТАБ: Податкові рахунки (отримувач ПДФО/ВЗ/ЄСВ для перерахування) === */}
        {activeTab === 'tax' && (
          <div className="tabs__panel">
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
              Реквізити отримувача для перерахування податків ЗП. Підтягуються у документ
              «Перерахування податків ЗП». МФО банку — автоматично з IBAN.
            </p>
            {([
              { key: 'pdfo', label: 'ПДФО (податок на доходи фізосіб)' },
              { key: 'wz', label: 'Військовий збір' },
              { key: 'esv', label: 'ЄСВ (єдиний соцвнесок)' },
            ] as const).map(({ key, label }) => {
              const nameField = `tax_${key}_recipient_name` as const;
              const ibanField = `tax_${key}_recipient_iban` as const;
              const iban = (form[ibanField] as string | null) ?? '';
              const mfo = extractMfoFromIban(iban);
              const bankName = findBankByIban(iban, banksQ.data ?? []);
              return (
                <fieldset key={key} style={{ border: '1px solid #e2e8f0', borderRadius: 6,
                  padding: '0.6rem 0.75rem', marginBottom: '0.6rem' }}>
                  <legend style={{ fontSize: '0.85rem', fontWeight: 600,
                    color: 'var(--text-secondary)', padding: '0 0.3rem' }}>{label}</legend>
                  <div className="field">
                    <label>Назва отримувача</label>
                    <input
                      value={(form[nameField] as string | null) ?? ''}
                      placeholder="Напр. ГУ ДПС у ..."
                      onChange={(e) => setForm({ ...form, [nameField]: e.target.value || null })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="field" style={{ flex: 2, minWidth: 240 }}>
                      <label>IBAN отримувача
                        {iban && (
                          <small style={{ color: isValidUaIban(iban) ? 'green' : '#d33', marginLeft: 6 }}>
                            {iban.replace(/\s/g, '').length}/29
                          </small>
                        )}
                      </label>
                      <input
                        value={iban}
                        placeholder="UA..."
                        maxLength={34}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 34);
                          setForm({ ...form, [ibanField]: v || null });
                        }}
                      />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 110 }}>
                      <label>МФО банку</label>
                      <input value={mfo ?? ''} readOnly disabled placeholder="з IBAN"
                        style={{ background: '#f8fafc' }} />
                    </div>
                  </div>
                  {bankName && (
                    <small style={{ color: 'var(--muted)' }}>🏦 {bankName}</small>
                  )}
                </fieldset>
              );
            })}
          </div>
        )}
      </Drawer>

      <ConfirmModuleNarrowingModal
        open={templateSwitchConfirm.open}
        title="Перемикання бізнес-шаблону юрособи"
        removedModules={templateSwitchConfirm.removedModules}
        affected={templateSwitchConfirm.affected}
        isLoading={templateSwitchConfirm.isLoading}
        onConfirm={confirmTemplateSwitch}
        onCancel={() =>
          setTemplateSwitchConfirm((s) => ({ ...s, open: false }))
        }
      />
    </>
  );
}


// ===========================================================================
// ORG-EDITOR.1 (2026-06-04): inline компоненти для табу «Банк».
// ===========================================================================

interface CurrencyOption {
  id: string;
  code: string;
  name: string;
}

interface BankAccountInlineFormProps {
  iban: string;
  onIbanChange: (v: string) => void;
  currencyId: string;
  onCurrencyChange: (id: string) => void;
  accountName: string;
  onAccountNameChange: (v: string) => void;
  /** Опціонально показати чекбокс "Основний рахунок" (тільки при створенні
   *  нового org або коли додаємо у org що ще без рахунків — інакше у списку
   *  є кнопка Зробити основним).
   */
  isMain?: boolean;
  onIsMainChange?: (v: boolean) => void;
  currencies: CurrencyOption[];
  banks: BankRow[];
  title: string;
  helpText?: string;
}

/** Форма для одного inline bank-account (при створенні org або при +Додати). */
function BankAccountInlineForm({
  iban, onIbanChange, currencyId, onCurrencyChange,
  accountName, onAccountNameChange,
  isMain, onIsMainChange,
  currencies, banks, title, helpText,
}: BankAccountInlineFormProps) {
  const bankName = findBankByIban(iban, banks);
  const ibanValid = isValidUaIban(iban);

  // Auto-default UAH currency через useEffect (не у render!).
  // 2026-06-04 hotfix: попередня версія робила setTimeout прямо у render
  // що викликало race condition і нескінченний loop при певних умовах.
  useEffect(() => {
    if (!currencyId && currencies.length > 0) {
      const uah = currencies.find((c) => c.code === 'UAH');
      if (uah) onCurrencyChange(uah.id);
    }
  }, [currencyId, currencies, onCurrencyChange]);

  return (
    <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
      <legend>{title}</legend>
      {helpText && (
        <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.9em' }}>
          {helpText}
        </p>
      )}
      <div className="field">
        <label>IBAN <small style={{ color: 'var(--muted)' }}>(UA + 27 цифр)</small></label>
        <input
          value={formatIban(iban)}
          placeholder="UA71 339500 00000 2600 8370 2300 01"
          onChange={(e) => onIbanChange(e.target.value)}
          style={{ fontFamily: 'monospace' }}
        />
        {iban.replace(/\s+/g, '').length > 0 && (
          <small style={{ color: ibanValid ? 'green' : '#d33' }}>
            {ibanValid
              ? bankName
                ? `✓ Банк: ${bankName}`
                : '✓ IBAN валідний (банк не у довіднику НБУ — введіть назву рахунку вручну)'
              : `✗ Формат UA + 27 цифр (зараз ${iban.replace(/\s+/g, '').length})`
            }
          </small>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Валюта *</label>
          <select
            value={currencyId}
            onChange={(e) => onCurrencyChange(e.target.value)}
          >
            <option value="">— Не обрано —</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Назва рахунку <small style={{ color: 'var(--muted)' }}>(опціонально)</small></label>
          <input
            value={accountName}
            placeholder={bankName ? `Основний рахунок ${bankName}` : 'Основний'}
            onChange={(e) => onAccountNameChange(e.target.value)}
          />
        </div>
      </div>
      {onIsMainChange !== undefined && (
        <div className="field" style={{ marginTop: '0.5rem' }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isMain ?? false}
              onChange={(e) => onIsMainChange(e.target.checked)}
              style={{ width: 'auto', marginRight: '0.5rem' }}
            />
            Основний рахунок організації
            <small style={{ color: 'var(--muted)', marginLeft: '0.5rem' }}>
              (за замовч. використовується у платежах)
            </small>
          </label>
        </div>
      )}
    </fieldset>
  );
}


interface BankAccountsEditListProps {
  accounts: BankAccount[];
  currencies: CurrencyOption[];
  banks: BankRow[];
  onAdd: (p: { iban: string; currency_id: string; account_name: string | null }) => void;
  onRemove: (id: string) => void;
  onSetMain: (id: string) => void;
  addPending: boolean;
}

/** Список наявних bank-accounts + форма для додавання нового. */
function BankAccountsEditList({
  accounts, currencies, banks,
  onAdd, onRemove, onSetMain, addPending,
}: BankAccountsEditListProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newIban, setNewIban] = useState('');
  const [newCurrencyId, setNewCurrencyId] = useState('');
  const [newName, setNewName] = useState('');

  const canAdd =
    isValidUaIban(newIban) && newCurrencyId !== '';

  function handleAdd() {
    if (!canAdd) return;
    onAdd({
      iban: newIban,
      currency_id: newCurrencyId,
      account_name: newName.trim() || null,
    });
    setNewIban(''); setNewCurrencyId(''); setNewName('');
    setShowAddForm(false);
  }

  return (
    <>
      {accounts.length === 0 && !showAddForm && (
        <p style={{ color: 'var(--muted)' }}>
          Немає банк-рахунків. Натисніть «+ Додати» щоб створити.
        </p>
      )}
      {accounts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.75rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '0.4rem 0.5rem', width: 60, textAlign: 'center' }}>Осн.</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>IBAN</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Назва</th>
              <th style={{ padding: '0.4rem 0.5rem', width: 80 }}>Валюта</th>
              <th style={{ padding: '0.4rem 0.5rem', width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((acc) => {
              const bankName = findBankByIban(acc.iban, banks);
              const curr = currencies.find((c) => c.id === acc.currency_id);
              return (
                <tr key={acc.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                    <input
                      type="radio"
                      name="bank-is-main"
                      checked={acc.is_main}
                      onChange={() => {
                        if (!acc.is_main) onSetMain(acc.id);
                      }}
                      title={acc.is_main ? 'Це основний рахунок' : 'Зробити основним'}
                      style={{ width: 'auto', cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {formatIban(acc.iban)}
                    {bankName && (
                      <div style={{ color: 'var(--muted)', fontSize: '0.9em' }}>
                        {bankName}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>
                    {acc.account_name ?? '—'}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}>{curr?.code ?? '—'}</td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Видалити рахунок ${formatIban(acc.iban)}?`)) {
                          onRemove(acc.id);
                        }
                      }}
                      style={{ color: '#d33' }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!showAddForm ? (
        <button type="button" onClick={() => setShowAddForm(true)}>
          + Додати банк-рахунок
        </button>
      ) : (
        <>
          <BankAccountInlineForm
            iban={newIban}
            onIbanChange={setNewIban}
            currencyId={newCurrencyId}
            onCurrencyChange={setNewCurrencyId}
            accountName={newName}
            onAccountNameChange={setNewName}
            currencies={currencies}
            banks={banks}
            title="Новий банк-рахунок"
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button type="button" onClick={() => setShowAddForm(false)}>
              Скасувати
            </button>
            <button
              type="button"
              className="primary"
              disabled={addPending}
              onClick={handleAdd}
              title={!canAdd ? 'Заповни IBAN (UA + 27 цифр) і вибери валюту' : undefined}
              style={{ opacity: canAdd ? 1 : 0.6 }}
            >
              {addPending ? 'Зберігаємо…' : 'Зберегти рахунок'}
            </button>
          </div>
        </>
      )}
    </>
  );
}
